var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { AsciiText } from './framework/AsciiText';
import { CommandSocketInitializer } from './framework/CommandSocketInitializer';
const stewartAudioChannelTypes = ['analogOutput', 'analogInput', 'danteInput'];
const suggestedTypes = {
    mics: stewartAudioChannelTypes,
    micsMuteStatus: stewartAudioChannelTypes,
    amplifier: stewartAudioChannelTypes,
    assistiveListeningMute: stewartAudioChannelTypes,
    assistiveListeningMuteStatus: stewartAudioChannelTypes,
    assistiveListeningVolume: stewartAudioChannelTypes,
    audioInputMute: stewartAudioChannelTypes,
    audioInputMuteStatus: stewartAudioChannelTypes,
    audioInputVolume: stewartAudioChannelTypes,
    overflowRoomMute: stewartAudioChannelTypes,
    overflowRoomMuteStatus: stewartAudioChannelTypes,
    overflowRoomVolume: stewartAudioChannelTypes,
    soundReinforcementMute: stewartAudioChannelTypes,
    soundReinforcementMuteStatus: stewartAudioChannelTypes,
    soundReinforcementVolume: stewartAudioChannelTypes,
    telephony: [],
    receiveGain: [],
    preset: [],
};
const rs232ChannelCommands = {
    analogInput: {
        getMute: 0x16,
        getVol: 0x15,
        setVol: 0x11,
        setMuteOn: 0x13,
        setMuteOff: 0x14,
    },
    analogOutput: {
        getMute: 0x0a,
        getVol: 0x09,
        setVol: 0x05,
        setMuteOn: 0x07,
        setMuteOff: 0x08,
    },
    danteInput: {
        getMute: 0x16,
        getVol: 0x15,
        setVol: 0x11,
        setMuteOn: 0x13,
        setMuteOff: 0x14,
    },
};
const telnetChannelCommands = {
    analogInput: {
        getMute: 0x5d,
        getVol: 0x5c,
        setMuteOff: 0x5b,
        setMuteOn: 0x5a,
        setVol: 0x58,
    },
    analogOutput: {
        getMute: 0x52,
        getVol: 0x33,
        setMuteOff: 0x51,
        setMuteOn: 0x50,
        setVol: 0x32,
    },
    danteInput: {
        getMute: 0x5d,
        getVol: 0x5c,
        setMuteOff: 0x5b,
        setMuteOn: 0x5a,
        setVol: 0x58,
    },
};
export class StewartAudioFlxE {
    constructor(socket, state, log, framework) {
        this.socket = socket;
        this.state = state;
        this.log = log;
        // other class properties and constructor
        this.channels = {};
        this.channelCommands = telnetChannelCommands;
        this.modelTable = [
            'Invalid Device Type',
            'FLX-E 80 4 LZ',
            'FLX-E 80 4 CV',
            'FLX-E 160 2 LZ',
            'FLX-E 160 2 CV',
            'FLX-E 320 1 CV',
            'FLX-E 80 4 LZ Dante',
            'FLX-E 80 4 CV Dante',
            'FLX-E 160 2 LZ Dante',
            'FLX-E 160 2 CV Dante',
            'FLX-E 320 1 CV Dante',
        ];
        this.model = this.modelTable[7];
        // controls
        this.detected = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.makeDetectedResults();
            }),
        };
        this.mute = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                const channel = this.channels[key];
                if (channel === undefined)
                    throw new Error(`no channel for key ${key}`);
                const type = channel.type;
                const muteCmd = this.channelCommands[type].getMute;
                const [cmd, regExp] = this.makeCommandAndRegExp([muteCmd, channel.number]);
                const match = yield this.socket.send(cmd, regExp, tags);
                return match[1] ? match[1].charCodeAt(0) === 1 : this.state.mutes.values[key];
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                const channel = this.channels[key];
                if (channel === undefined)
                    throw new Error(`no channel for key ${key}`);
                const type = channel.type;
                const muteOnCmd = this.channelCommands[type].setMuteOn;
                const muteOffCmd = this.channelCommands[type].setMuteOff;
                const muteCmd = value ? muteOnCmd : muteOffCmd;
                // The device returns the channel number even though the API docs don't show it.
                const [cmd, regExp] = this.makeCommandAndRegExp([muteCmd, channel.number]);
                const match = yield this.socket.send(cmd, regExp, tags);
                return match[1] ? match[1].charCodeAt(0) === 1 : this.state.mutes.values[key];
            }),
            getKey: this.getKey.bind(this),
            validate: this.validate.bind(this), // happens before constructor binding
        };
        this.volume = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                const channel = this.channels[key];
                if (channel === undefined)
                    throw new Error(`no channel for key ${key}`);
                const type = channel.type;
                const volCmd = this.channelCommands[type].getVol;
                const [cmd, regExp] = this.makeCommandAndRegExp([volCmd, channel.number]);
                const match = yield this.socket.send(cmd, regExp, tags);
                if (match[1])
                    return -1 * match[1].charCodeAt(0);
                return this.state.volumes.values[key];
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                const channel = this.channels[key];
                if (channel === undefined)
                    throw new Error(`no channel for key ${key}`);
                const type = channel.type;
                const volCmd = this.channelCommands[type].setVol;
                const [cmd, regExp] = this.makeCommandAndRegExp([volCmd, channel.number, -1 * value]);
                const match = yield this.socket.send(cmd, regExp, tags);
                if (match[1])
                    return -1 * match[1].charCodeAt(0);
                return this.state.volumes.values[key];
            }),
            getKey: this.getKey.bind(this),
            range: (config, info) => __awaiter(this, void 0, void 0, function* () {
                if (info.volumeType === 'trim')
                    return { max: 0, min: -24, round: 1 };
                return { max: 0, min: -50, round: 1 }; // volume
            }),
            validate: this.validate.bind(this), // happens before constructor binding
        };
        // polling
        this.polls = [
            {
                intervalMs: 4000,
                controls: ['mute', 'volume'],
            },
            {
                // Keep alive poll in case no channels are defined on the DSP.
                intervalMs: 4000,
                function: () => __awaiter(this, void 0, void 0, function* () {
                    const tags = ['poll'];
                    if (this.socket.port === 4999) {
                        const [cmd, regExp] = this.makeCommandAndRegExp([0x0a, 0x01]);
                        yield this.socket.send(cmd, regExp, tags);
                    }
                    else {
                        yield this.socket.send(this.makeCommand([0x03]), /\x1e\x02\x00\x00\x02\x04/, tags);
                    }
                }),
            },
            {
                intervalMs: 15000,
                function: () => __awaiter(this, void 0, void 0, function* () {
                    if (this.socket.port !== 4999) {
                        yield this.getAmpTemperature();
                    }
                }),
            },
        ];
    }
    // phase callbacks
    onInitialize(credentials) {
        return __awaiter(this, void 0, void 0, function* () {
            const result = yield CommandSocketInitializer.initialize(this.socket, [
                this.socket.port === 4999
                    ? {
                        errorCode: 'verifyFailed',
                        send: '\x02\x0A\x01',
                        succeed: /\x02\x0A\x01[\s\S]\x03|\x02\x00\x00\x00\x03/,
                    }
                    : {
                        errorCode: 'verifyFailed',
                        send: '\x1e\x02\x00\x00\x03\x05',
                        succeed: /\x1e\x02\x00\x00\x02\x04/,
                    },
            ], credentials);
            if (result !== 'connected')
                return result;
            let numInputs = 4;
            let numOutputs = 1;
            let numDanteInputs = 0;
            if (this.socket.port !== 4999) {
                this.channelCommands = telnetChannelCommands;
                // Get the model name when connected over telnet.
                try {
                    // Retry twice because sometimes the device doesn't respond the first time.
                    const match = yield this.socket.send(this.makeCommand([0x05]), /\x1e[\s\S]{3}\x05[\s\S]{9}([\s\S])[\s\S]+/, ['onInitialize', 'getModel'], { retryAttempts: 2 });
                    this.model = this.modelTable[match[1].charCodeAt(0)];
                }
                catch (err) {
                    this.log.warning(`could not get model name: ${err}`);
                }
                // Determine the numbers of inputs/outputs from the product name.
                const modelRegEx = RegExp('FLX-E (\\d+) (\\d+) (?:LZ|CV)\\s*(Dante)?', 'i');
                const match = modelRegEx.exec(this.model);
                if (match) {
                    numInputs = 4;
                    numOutputs = parseInt(match[2], 10);
                    numDanteInputs = match[3] ? (match[3].toLowerCase() === 'dante' ? 4 : 0) : 0;
                }
            }
            else {
                this.channelCommands = rs232ChannelCommands;
                // Determine the numbers of inputs/outputs by sending mute query commands.
                try {
                    const cmds = this.channelCommands;
                    numInputs = yield this.detectNumChannels(cmds.analogInput.getMute, 1, 4);
                    numDanteInputs = yield this.detectNumChannels(cmds.danteInput.getMute, 5, 4);
                    numOutputs = yield this.detectNumChannels(cmds.analogOutput.getMute, 1, 4);
                }
                catch (err) {
                    this.log.warning(`could not get channel info: ${err}`);
                }
            }
            this.channels = {};
            for (let i = 0; i < numInputs; i++) {
                const key = `analogInput.${i + 1}`;
                this.channels[key] = {
                    type: 'analogInput',
                    number: i + 1,
                    name: `Input ${i + 1}`,
                };
            }
            for (let i = 0; i < numDanteInputs; i++) {
                const key = `danteInput.${i + 1}`;
                this.channels[key] = {
                    type: 'danteInput',
                    // Dante inputs are labeled as 1, 2, etc. but in commands they use channel numbers
                    // starting after the last analog input.
                    number: numInputs + i + 1,
                    name: `Dante Input ${i + 1}`,
                };
            }
            for (let i = 0; i < numOutputs; i++) {
                const key = `analogOutput.${i + 1}`;
                this.channels[key] = {
                    type: 'analogOutput',
                    number: i + 1,
                    name: `Output ${i + 1}`,
                };
            }
            return result;
        });
    }
    // other class methods
    detectNumChannels(query, start, max) {
        return __awaiter(this, void 0, void 0, function* () {
            // Use mute queries to detect the number of channels.
            for (let i = 0; i < max; i++) {
                const chanNum = start + i;
                const [cmd, regExp] = this.makeCommandAndRegExp([query, chanNum]);
                const tags = ['initialize', 'detectChannel'];
                const match = yield this.socket.send(cmd, regExp, tags);
                if (match[1] === undefined)
                    return i;
            }
            return max;
        });
    }
    // Support for future Amp tempoerature monitoring.
    getAmpTemperature() {
        return __awaiter(this, void 0, void 0, function* () {
            const match = yield this.socket.send(this.makeCommand([0x05]), /\x1e[\s\S]{3}\x05[\s\S]([\s\S]{4})([\s\S]{4})[\s\S]+/, ['poll', 'status']);
            const ampTempByteArray = new Uint8Array([
                match[1].charCodeAt(0),
                match[1].charCodeAt(1),
                match[1].charCodeAt(2),
                match[1].charCodeAt(3),
            ]);
            const mcuTempByteArray = new Uint8Array([
                match[2].charCodeAt(0),
                match[2].charCodeAt(1),
                match[2].charCodeAt(2),
                match[2].charCodeAt(3),
            ]);
            const ampTemp = new Float32Array(ampTempByteArray.buffer);
            const mcuTemp = new Float32Array(mcuTempByteArray.buffer);
            this.log.debug(`amp temp = ${ampTemp[0]}, mcu temp = ${mcuTemp[0]}`);
        });
    }
    getKey(config) {
        return config;
    }
    makeDetectedResults() {
        const detected = [];
        for (const [key, channel] of Object.entries(this.channels)) {
            detected.push({
                channel: key,
                filters: this.makeFilters(channel.type),
                name: channel.name,
                type: channel.type,
            });
        }
        return detected;
    }
    makeCommand(data) {
        const output = [];
        if (this.socket.port === 4999) {
            output.push(0x02); // command header
            output.push(...data);
        }
        else {
            output.push(0x1e); // command header
            const length = data.length + 1; // add one for the message sequence number
            output.push(length & 0x00ff); // low byte of payload length
            output.push((length & 0xff00) >> 8); // high byte of payload length
            output.push(0x00); // message sequence number
            const checksum = data.reduce((sum, current) => sum + current, length);
            output.push(...data);
            output.push(checksum & 0x00ff);
        }
        return AsciiText.decode(output);
    }
    makeCommandAndRegExp(data) {
        if (data.length < 2)
            throw new Error(`expected at least two bytes`);
        let regExpStr = '';
        if (this.socket.port === 4999) {
            regExpStr = `\\x02\\x${data[0].toString(16).padStart(2, '0')}`;
            regExpStr += `\\x${data[1].toString(16).padStart(2, '0')}([\\s\\S])\\x03`;
            regExpStr += `|\\x02[\\x18\\x00]\\x00\\x00\\x03`; // error response
        }
        else {
            regExpStr = `\\x1e\\x04\\x00\\x00`; // assumes payload length is 0x04
            regExpStr += `\\x${data[0].toString(16).padStart(2, '0')}`;
            regExpStr += `\\x${data[1].toString(16).padStart(2, '0')}([\\s\\S])[\\s\\S]`;
            regExpStr += `|\\x1e\\x02\\x00[\\s\\S][\\x03\\x02][\\s\\S]`; // error response
        }
        return [this.makeCommand(data), new RegExp(regExpStr)];
    }
    makeFilters(type) {
        const filters = {};
        for (const [key, types] of Object.entries(suggestedTypes)) {
            filters[key] = types.includes(type);
        }
        return filters;
    }
    validate(config, info) {
        return __awaiter(this, void 0, void 0, function* () {
            const key = this.getKey(config);
            const channel = this.channels[key];
            if (channel === undefined) {
                this.log.error(`no channel for config: ${config}`);
                return 'missing';
            }
            return 'ok';
        });
    }
}
// static properties
StewartAudioFlxE.channelsConfigName = 'stewartAudioControlChannels';
StewartAudioFlxE.driverType = 'socketDriver';
StewartAudioFlxE.key = '-Ms1XjuvhKuiKlhMWeC_';
StewartAudioFlxE.baseSocketOptionsTelnet = {
    commandExtractor: (buffer) => {
        // response: 0x1e lengthLo lengthHi id cmd data1 ... dataN checksum
        for (let i = 0; i < buffer.length; i++) {
            let checksum = 0;
            if (buffer.charCodeAt(i) === 0x1e) {
                let payloadLength = (buffer.charCodeAt(i + 2) << 8) + buffer.charCodeAt(i + 1);
                if (payloadLength < buffer.length - 3) {
                    for (let j = i + 1; j < i + payloadLength + 3; j++) {
                        checksum += buffer.charCodeAt(j);
                    }
                    checksum = checksum & 0x00ff;
                    if (checksum === buffer.charCodeAt(i + payloadLength + 3)) {
                        const command = buffer.slice(i, i + payloadLength + 4);
                        buffer = buffer.slice(i + payloadLength + 4);
                        return { command, buffer };
                    }
                }
            }
        }
        return undefined;
    },
    sendTerminator: '',
    isBinary: true,
};
StewartAudioFlxE.baseSocketOptionsRs232 = {
    commandExtractor: (buffer) => {
        // response: 0x02 cmd channel value 0x03
        for (let i = 0; i < buffer.length - 4; i++) {
            if (buffer.charCodeAt(i) === 0x02 && buffer.charCodeAt(i + 4) === 0x03) {
                const command = buffer.slice(i, i + 5);
                buffer = buffer.slice(i + 5);
                return { command, buffer };
            }
        }
        return undefined;
    },
    sendTerminator: '\x03',
    isBinary: true,
};
StewartAudioFlxE.socketOptions = [
    Object.assign(Object.assign({}, StewartAudioFlxE.baseSocketOptionsTelnet), { port: 50001 }),
    Object.assign(Object.assign({}, StewartAudioFlxE.baseSocketOptionsRs232), { port: 4999 }),
    Object.assign(Object.assign({}, StewartAudioFlxE.baseSocketOptionsTelnet), { port: 50002 }),
    Object.assign(Object.assign({}, StewartAudioFlxE.baseSocketOptionsTelnet), { port: 50003 }),
];
